import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { courses as coursesApi } from "@/lib/api";
import { Printer, ArrowLeft, Check, Clock, BarChart3 } from "lucide-react";

const Syllabus = () => {
  const [list, setList] = useState([]);

  useEffect(() => {
    coursesApi.list().then(setList).catch(() => setList([]));
    document.title = "Temario completo — Kursos Online";
  }, []);

  return (
    <main data-testid="syllabus-page" className="bg-white min-h-screen">
      {/* Toolbar - hidden when printing */}
      <div className="no-print sticky top-0 z-10 bg-white border-b border-zinc-200 px-6 py-4 flex items-center justify-between">
        <Link to="/" className="text-sm text-zinc-600 hover:text-zinc-900 inline-flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Volver
        </Link>
        <button
          onClick={() => window.print()}
          data-testid="syllabus-print-btn"
          className="inline-flex items-center gap-2 bg-[#4F46E5] hover:bg-[#4338CA] text-white px-5 py-2.5 font-semibold text-sm"
        >
          <Printer className="w-4 h-4" /> Imprimir / Guardar PDF
        </button>
      </div>

      <article className="max-w-4xl mx-auto px-6 md:px-12 py-12 md:py-16 print:py-6">
        <header className="mb-12 pb-8 border-b-2 border-zinc-900">
          <div className="flex items-center gap-2 mb-4">
            <div className="h-7 w-7 bg-[#4F46E5] flex items-center justify-center">
              <span className="text-white font-black text-sm font-display">K</span>
            </div>
            <span className="font-display font-black text-lg tracking-tight">Kursos·Online</span>
          </div>
          <h1 className="font-display font-black text-4xl md:text-6xl tracking-tighter leading-[0.95] mb-4">
            Temario completo de cursos
          </h1>
          <p className="text-zinc-600 text-lg">
            Plataforma educativa premium para emprendedores hispanohablantes en América Latina.
          </p>
          <p className="text-xs uppercase tracking-wider text-zinc-500 mt-4 font-semibold">
            Documento generado el {new Date().toLocaleDateString("es-MX", { year: "numeric", month: "long", day: "numeric" })}
          </p>
        </header>

        {list.map((c, i) => (
          <section
            key={c.id}
            data-testid={`syllabus-course-${c.id}`}
            className="mb-16 print:mb-8 print:break-inside-avoid"
          >
            <div className="flex items-start justify-between gap-4 mb-6">
              <div className="flex-1">
                <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#4F46E5] mb-2">
                  Curso {String(i + 1).padStart(2, "0")}
                </div>
                <h2 className="font-display font-black text-3xl md:text-4xl tracking-tighter leading-tight mb-3">
                  {c.title}
                </h2>
                <p className="text-zinc-600 italic">{c.subtitle}</p>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">
                  Precio
                </div>
                <div className="font-display font-black text-4xl">${c.price}</div>
                <div className="text-xs text-zinc-500">{c.currency?.toUpperCase()}</div>
              </div>
            </div>

            <div className="flex items-center gap-5 text-sm text-zinc-600 mb-6 pb-6 border-b border-zinc-200">
              <span className="inline-flex items-center gap-1.5">
                <Clock className="w-4 h-4" /> {c.duration}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <BarChart3 className="w-4 h-4" /> Nivel {c.level}
              </span>
              <span className="inline-flex items-center gap-1.5">
                📚 {c.modules?.length || 0} módulos
              </span>
            </div>

            <p className="text-zinc-700 leading-relaxed mb-6">{c.description}</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xs uppercase tracking-[0.2em] font-bold mb-4">
                  Contenido del curso
                </h3>
                <ol className="space-y-3">
                  {(c.modules || []).map((m, idx) => (
                    <li key={`${c.id}-mod-${idx}`} className="flex gap-3">
                      <span className="font-mono text-xs text-zinc-400 pt-1 flex-shrink-0">
                        {String(idx + 1).padStart(2, "0")}
                      </span>
                      <div>
                        <div className="font-semibold text-zinc-900">{m.title}</div>
                        <div className="text-xs text-zinc-500">
                          {m.lessons} lecciones · {m.duration}
                        </div>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>

              <div>
                <h3 className="text-xs uppercase tracking-[0.2em] font-bold mb-4">Incluye</h3>
                <ul className="space-y-3">
                  {(c.highlights || []).map((h, idx) => (
                    <li key={`${c.id}-h-${idx}`} className="flex items-start gap-2 text-sm">
                      <Check className="w-4 h-4 text-[#4F46E5] mt-0.5 flex-shrink-0" />
                      <span className="text-zinc-700">{h}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>
        ))}

        <footer className="mt-20 pt-8 border-t-2 border-zinc-900 text-center print:mt-8">
          <p className="text-sm text-zinc-500">
            © {new Date().getFullYear()} Kursos Online · Todos los cursos incluyen garantía de 7 días
          </p>
          <p className="text-xs text-zinc-400 mt-2 font-mono">kursos-online.com</p>
        </footer>
      </article>

      <style>{`
        @media print {
          .no-print { display: none !important; }
          @page { margin: 1.5cm; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>
    </main>
  );
};

export default Syllabus;
